import { useState, useCallback } from 'react';
import { Sparkles, AlertTriangle, Clock, LogIn } from 'lucide-react';
import { useFooterVisibility } from '../hooks/useFooterVisibility';
import { useSubscriptionStore, selectAuthStale } from '../store/subscriptionStore';
import { UpgradeDialog } from './UpgradeDialog';
import { AuthDialog } from './AuthDialog';
import { recordAuthIntentSource } from '../services/signInIntent';

/**
 * Footer bar shown for license status indication.
 *
 * Visibility rules:
 * - Shown during active trial with days remaining
 * - Shown in Personal mode (trial skipped/never started/expired)
 * - Hidden when Pro subscription is active
 * - Shows grace period countdown when subscription expired but within 7 days
 * - Shows re-sign-in prompt when auth is stale (zombie state)
 */
export function PersonalModeFooter() {
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const { visible, mode, gracePeriodDays, trialDaysRemaining } = useFooterVisibility();
  const authStale = useSubscriptionStore(selectAuthStale);

  const handleUpgradeClick = useCallback(() => {
    setUpgradeOpen(true);
  }, []);

  const handleSignInClick = useCallback(() => {
    recordAuthIntentSource('footer');
    setAuthDialogOpen(true);
  }, []);

  if (!visible && !authStale) {
    return null;
  }

  // Auth stale takes priority — user may have Pro but we can't verify
  if (authStale && mode === 'personal') {
    return (
      <>
        <div
          data-testid="personal-mode-footer"
          className="flex items-center justify-center gap-2 px-3 py-1.5 bg-amber-50 dark:bg-amber-950/30 border-t border-amber-200 dark:border-amber-800 text-sm text-amber-700 dark:text-amber-400 select-none"
        >
          <AlertTriangle className="h-4 w-4" />
          <span>Session expired — sign in to restore your subscription</span>
          <span className="text-amber-400/50 dark:text-amber-500/50">·</span>
          <button
            onClick={handleSignInClick}
            className="inline-flex items-center gap-1 font-medium hover:underline transition-colors"
          >
            <LogIn className="h-3.5 w-3.5" />
            Sign In
          </button>
        </div>
        <AuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} />
      </>
    );
  }

  if (!visible) {
    return null;
  }

  const renderContent = () => {
    switch (mode) {
      case 'trial':
        return (
          <>
            <Clock className="h-4 w-4 text-primary" />
            <span>
              Pro Trial — {trialDaysRemaining}{' '}
              {trialDaysRemaining === 1 ? 'day' : 'days'} left
            </span>
          </>
        );
      case 'grace-period':
        return (
          <>
            <AlertTriangle className="h-4 w-4 text-warning" />
            <span>
              Subscription expired. Renew within {gracePeriodDays}{' '}
              {gracePeriodDays === 1 ? 'day' : 'days'}
            </span>
          </>
        );
      default:
        return (
          <span>
            For personal,{' '}
            <span className="font-semibold underline">non-commercial</span> use
            only
          </span>
        );
    }
  };

  return (
    <>
      <div
        data-testid="personal-mode-footer"
        className="flex items-center justify-center gap-2 px-3 py-1.5 bg-muted/50 border-t border-border text-sm text-muted-foreground select-none"
      >
        {renderContent()}
        <span className="text-muted-foreground/50">·</span>
        <button
          onClick={handleUpgradeClick}
          className="inline-flex items-center gap-1 text-primary hover:text-primary/80 hover:underline transition-colors"
        >
          <Sparkles className="h-3.5 w-3.5" />
          Upgrade
        </button>
      </div>
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} />
    </>
  );
}
